import { useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  getAbandon,
  getAgentDays,
  getWaitBetweenCalls,
  type AbandonRow,
  type AgentDay,
  type RangeParams,
  type ShiftQuality,
  type WorkforceMeta,
} from "../api/workforce";
import { fmtDateTimeInZone, fmtHms, fmtHour, fmtMs, fmtTimeInZone, localDateInZone } from "../lib/time";
import { WorkforceTransfersTab } from "./WorkforceTransfersTab";

// Five reports, one page. Every tab shows its figures AND how they are
// computed (the definitions come from the API, so the text next to the number
// is the same text the code was written from). People are paid from the
// Payroll tab — nothing on it is allowed to look more certain than it is.

type Tab = "payroll" | "hours" | "calls" | "abandon" | "gaps" | "transfers";

const TABS: { id: Tab; label: string }[] = [
  { id: "payroll", label: "Payroll" },
  { id: "hours", label: "Hours vs Output" },
  { id: "calls", label: "Calls per Agent" },
  { id: "abandon", label: "Abandon Rate" },
  { id: "gaps", label: "Wait Between Calls" },
  { id: "transfers", label: "Transfers" },
];

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

const QUALITY_LABEL: Record<ShiftQuality, string> = {
  clean: "Signed out",
  console_died: "Console died",
  system_signout: "System sign-out",
  floor: "Floor — not a measurement",
  open: "Still open",
  none: "No shift record",
};

const QUALITY_CLASS: Record<ShiftQuality, string> = {
  clean: "bg-green-100 text-green-800",
  console_died: "bg-amber-100 text-amber-800",
  system_signout: "bg-amber-100 text-amber-800",
  floor: "bg-red-100 text-red-800",
  open: "bg-blue-100 text-blue-800",
  none: "bg-gray-200 text-gray-600",
};

function QualityBadge({ quality }: { quality: ShiftQuality }) {
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${QUALITY_CLASS[quality] ?? "bg-gray-200 text-gray-600"}`}
      title={QUALITY_LABEL[quality] ?? quality}
    >
      {QUALITY_LABEL[quality] ?? quality}
    </span>
  );
}

function Definitions({
  meta,
  keys,
}: {
  meta: WorkforceMeta | undefined;
  keys: { key: string; label: string }[];
}) {
  if (!meta) return null;
  return (
    <details className="mb-3 bg-white rounded-lg shadow px-4 py-3 text-sm">
      <summary className="cursor-pointer font-medium text-gray-700">How these figures are computed</summary>
      <dl className="mt-2 grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
        {keys.map(({ key, label }) => (
          <div key={key} className="contents">
            <dt className="font-medium text-gray-700">{label}</dt>
            <dd className="text-gray-600">{meta.definitions[key] ?? "—"}</dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

function csvEscape(v: unknown): string {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, header: string[], rows: unknown[][]) {
  const body = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtDay(iso: string): string {
  // "2026-09-18" → "Thu Sep 18" without ever passing through a Date's local zone.
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" }).format(dt);
}

function EmptyRow({ cols, text }: { cols: number; text: string }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-6 text-center text-gray-400">
        {text}
      </td>
    </tr>
  );
}

function ShiftTrackingNote({ meta }: { meta: WorkforceMeta | undefined }) {
  if (!meta) return null;
  if (!meta.shift_tracking_since) {
    return (
      <p className="text-sm text-amber-700 mb-3">
        No shift records exist yet. Hours cannot be reported until agents have signed in since shift
        tracking was enabled.
      </p>
    );
  }
  const since = fmtDateTimeInZone(meta.shift_tracking_since, meta.timezone);
  return (
    <p className="text-sm text-gray-500 mb-3">
      Hours are measured from shift records, which begin {since}. Days before that show no shift
      time even if calls were taken.
    </p>
  );
}

// ---------------------------------------------------------------------------
// R2 Payroll
// ---------------------------------------------------------------------------

function PayrollTab({ rows, meta }: { rows: AgentDay[]; meta: WorkforceMeta | undefined }) {
  const [openRow, setOpenRow] = useState<string | null>(null);
  const tz = meta?.timezone ?? "UTC";
  return (
    <div>
      <Definitions
        meta={meta}
        keys={[
          { key: "talk", label: "Talk time" },
          { key: "wait", label: "Wait time" },
          { key: "disposition", label: "Disposition time" },
          { key: "active", label: "Active time" },
          { key: "pause", label: "Pause time" },
          { key: "on_shift", label: "On shift" },
          { key: "shift_quality", label: "Shift quality" },
          { key: "calls_outside_shift", label: "Calls outside shift" },
        ]}
      />
      <ShiftTrackingNote meta={meta} />
      <div className="flex justify-end mb-2">
        <button
          type="button"
          className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-sm"
          disabled={rows.length === 0}
          onClick={() =>
            downloadCsv(
              `payroll_${meta?.date_from}_${meta?.date_to}.csv`,
              ["Date", "Rep", "Talk", "Wait", "Disposition", "Active", "Pause", "On shift", "Shift quality", "Calls outside shift", "Wait floored"],
              rows.map((r) => [
                r.day, r.agent_name, fmtHms(r.talk_s), fmtHms(r.wait_s), fmtHms(r.disposition_s),
                fmtHms(r.active_s), fmtHms(r.pause_s), fmtHms(r.on_shift_s), r.shift_quality,
                r.calls_outside_shift, r.wait_floored ? "yes" : "",
              ]),
            )
          }
        >
          Download CSV
        </button>
      </div>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Rep</th>
              <th className="px-4 py-2 text-right">Talk</th>
              <th className="px-4 py-2 text-right">Wait</th>
              <th className="px-4 py-2 text-right">Disposition</th>
              <th className="px-4 py-2 text-right font-semibold">Active</th>
              <th className="px-4 py-2 text-right">Pause</th>
              <th className="px-4 py-2 text-right">On shift</th>
              <th className="px-4 py-2">Shift quality</th>
              <th className="px-4 py-2">Flags</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && <EmptyRow cols={10} text="No activity in this range." />}
            {rows.map((r) => {
              const id = `${r.day}:${r.agent_key}`;
              const flags: string[] = [];
              if (r.wait_floored) flags.push("Talk exceeds shift time — wait shown as 0");
              if (r.calls_outside_shift > 0) flags.push(`${r.calls_outside_shift} call(s) outside shift`);
              if (r.dispositions > r.dispositions_paired)
                flags.push(`${r.dispositions - r.dispositions_paired} disposition(s) unpaired (0s)`);
              return (
                <>
                  <tr key={id} className={r.wait_floored ? "bg-red-50" : undefined}>
                    <td className="px-4 py-2 whitespace-nowrap">{fmtDay(r.day)}</td>
                    <td className="px-4 py-2 font-medium">
                      <button
                        type="button"
                        className="text-left hover:underline"
                        onClick={() => setOpenRow(openRow === id ? null : id)}
                        aria-expanded={openRow === id}
                      >
                        {r.agent_name}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{fmtHms(r.talk_s)}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtHms(r.wait_s)}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtHms(r.disposition_s)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">{fmtHms(r.active_s)}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtHms(r.pause_s)}</td>
                    <td className="px-4 py-2 text-right font-mono">{fmtHms(r.on_shift_s)}</td>
                    <td className="px-4 py-2">
                      <QualityBadge quality={r.shift_quality} />
                    </td>
                    <td className="px-4 py-2 text-xs text-amber-800">{flags.join("; ") || ""}</td>
                  </tr>
                  {openRow === id && (
                    <tr key={`${id}-shifts`} className="bg-gray-50">
                      <td colSpan={10} className="px-6 py-2 text-xs text-gray-600">
                        {r.shifts.length === 0 ? (
                          <span>No shift record on this day.</span>
                        ) : (
                          <ul className="space-y-0.5">
                            {r.shifts.map((s) => (
                              <li key={`${s.shift_id}-${s.start}`}>
                                Shift #{s.shift_id}: {fmtTimeInZone(s.start, tz)} – {fmtTimeInZone(s.end, tz)} ·{" "}
                                <QualityBadge quality={s.quality} />
                                {s.ended_by ? <span className="ml-1 text-gray-400">(ended_by: {s.ended_by})</span> : null}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// R3 Hours vs Output
// ---------------------------------------------------------------------------

function HoursTab({ rows, meta }: { rows: AgentDay[]; meta: WorkforceMeta | undefined }) {
  return (
    <div>
      <Definitions
        meta={meta}
        keys={[
          { key: "payable_hours", label: "Hours on" },
          { key: "calls_connected", label: "Calls produced" },
          { key: "shift_quality", label: "Shift quality" },
          { key: "calls_outside_shift", label: "Calls outside shift" },
        ]}
      />
      <ShiftTrackingNote meta={meta} />
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Rep</th>
              <th className="px-4 py-2 text-right">Hours on</th>
              <th className="px-4 py-2 text-right">On shift</th>
              <th className="px-4 py-2 text-right">Calls produced</th>
              <th className="px-4 py-2 text-right">Calls / hour</th>
              <th className="px-4 py-2 text-right">Outside shift</th>
              <th className="px-4 py-2">Shift quality</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && <EmptyRow cols={8} text="No activity in this range." />}
            {rows.map((r) => (
              <tr key={`${r.day}:${r.agent_key}`}>
                <td className="px-4 py-2 whitespace-nowrap">{fmtDay(r.day)}</td>
                <td className="px-4 py-2 font-medium">{r.agent_name}</td>
                <td className="px-4 py-2 text-right font-mono">{r.payable_hours.toFixed(2)}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtHms(r.on_shift_s)}</td>
                <td className="px-4 py-2 text-right font-mono">{r.calls_connected}</td>
                <td className="px-4 py-2 text-right font-mono">
                  {r.calls_per_hour === null ? "—" : r.calls_per_hour.toFixed(2)}
                </td>
                <td className={`px-4 py-2 text-right font-mono ${r.calls_outside_shift > 0 ? "text-amber-800" : ""}`}>
                  {r.calls_outside_shift}
                </td>
                <td className="px-4 py-2">
                  <QualityBadge quality={r.shift_quality} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// R8 Calls per agent per day
// ---------------------------------------------------------------------------

function CallsTab({ rows, meta }: { rows: AgentDay[]; meta: WorkforceMeta | undefined }) {
  return (
    <div>
      <Definitions
        meta={meta}
        keys={[
          { key: "calls_connected", label: "Connected" },
          { key: "calls_completed", label: "Completed" },
          { key: "missed_rings", label: "Missed rings" },
          { key: "talk", label: "Talk time" },
        ]}
      />
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Rep</th>
              <th className="px-4 py-2 text-right">Connected</th>
              <th className="px-4 py-2 text-right">Completed</th>
              <th className="px-4 py-2 text-right">Dispositions</th>
              <th className="px-4 py-2 text-right">Missed rings</th>
              <th className="px-4 py-2 text-right">Talk time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && <EmptyRow cols={7} text="No calls in this range." />}
            {rows.map((r) => (
              <tr key={`${r.day}:${r.agent_key}`}>
                <td className="px-4 py-2 whitespace-nowrap">{fmtDay(r.day)}</td>
                <td className="px-4 py-2 font-medium">{r.agent_name}</td>
                <td className="px-4 py-2 text-right font-mono">{r.calls_connected}</td>
                <td className="px-4 py-2 text-right font-mono">{r.calls_completed}</td>
                <td className="px-4 py-2 text-right font-mono">{r.dispositions}</td>
                <td className="px-4 py-2 text-right font-mono">{r.missed_rings}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtHms(r.talk_s)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// R4 Abandon rate
// ---------------------------------------------------------------------------

function pct(v: number | null): string {
  return v === null ? "—" : `${v.toFixed(1)}%`;
}

function AbandonTable({ rows, first, firstLabel }: { rows: AbandonRow[]; first: (r: AbandonRow) => string; firstLabel: string }) {
  return (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-500">
          <tr>
            <th className="px-4 py-2">{firstLabel}</th>
            <th className="px-4 py-2 text-right">Entered</th>
            <th className="px-4 py-2 text-right">Connected</th>
            <th className="px-4 py-2 text-right">Abandoned</th>
            <th className="px-4 py-2 text-right">Timed out</th>
            <th className="px-4 py-2 text-right font-semibold">Abandon %</th>
            <th className="px-4 py-2 text-right">Drop %</th>
            <th className="px-4 py-2 text-right">Avg wait before abandon</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.length === 0 && <EmptyRow cols={8} text="No queue activity in this range." />}
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="px-4 py-2 font-medium whitespace-nowrap">{first(r)}</td>
              <td className="px-4 py-2 text-right font-mono">{r.entered}</td>
              <td className="px-4 py-2 text-right font-mono">{r.connected}</td>
              <td className="px-4 py-2 text-right font-mono">{r.abandoned}</td>
              <td className="px-4 py-2 text-right font-mono">{r.timed_out}</td>
              <td className="px-4 py-2 text-right font-mono font-semibold">{pct(r.abandon_pct)}</td>
              <td className="px-4 py-2 text-right font-mono">{pct(r.drop_pct)}</td>
              <td className="px-4 py-2 text-right font-mono">{fmtMs(r.avg_abandon_wait_s)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AbandonTab({ range }: { range: RangeParams }) {
  const [campaignId, setCampaignId] = useState<number | undefined>(undefined);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["wf-abandon", range, campaignId],
    queryFn: () => getAbandon({ ...range, campaign_id: campaignId }),
    placeholderData: keepPreviousData,
  });
  if (isLoading && !data) return <p className="text-gray-500 text-sm">Loading…</p>;
  if (isError) return <p className="text-red-600 text-sm">{(error as Error).message}</p>;
  const meta = data?.meta;
  return (
    <div>
      <Definitions
        meta={meta}
        keys={[
          { key: "abandon_pct", label: "Abandon %" },
          { key: "drop_pct", label: "Drop %" },
        ]}
      />
      <h2 className="text-lg font-semibold mb-2">By campaign</h2>
      <AbandonTable
        rows={data?.by_campaign ?? []}
        firstLabel="Campaign"
        first={(r) => r.campaign_name ?? (r.campaign_id != null ? `Campaign ${r.campaign_id}` : "Unknown queue")}
      />
      <div className="flex items-center justify-between mt-6 mb-2">
        <h2 className="text-lg font-semibold">By hour</h2>
        <label className="text-sm text-gray-600">
          Campaign{" "}
          <select
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={campaignId ?? ""}
            onChange={(e) => setCampaignId(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">All campaigns</option>
            {(data?.by_campaign ?? [])
              .filter((r) => r.campaign_id != null)
              .map((r) => (
                <option key={r.campaign_id!} value={r.campaign_id!}>
                  {r.campaign_name ?? `Campaign ${r.campaign_id}`}
                </option>
              ))}
          </select>
        </label>
      </div>
      <AbandonTable
        rows={data?.by_hour ?? []}
        firstLabel={`Hour (${meta?.timezone ?? ""})`}
        first={(r) => `${r.day ? fmtDay(r.day) : ""} ${r.hour != null ? fmtHour(r.hour) : ""}`}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// R5 Wait between calls
// ---------------------------------------------------------------------------

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-lg shadow px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function GapsTab({ range }: { range: RangeParams }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["wf-gaps", range],
    queryFn: () => getWaitBetweenCalls(range),
    placeholderData: keepPreviousData,
  });
  if (isLoading && !data) return <p className="text-gray-500 text-sm">Loading…</p>;
  if (isError) return <p className="text-red-600 text-sm">{(error as Error).message}</p>;
  const meta = data?.meta;
  const qw = data?.queue_wide;
  const hold = data?.caller_hold_overall;
  return (
    <div>
      <Definitions
        meta={meta}
        keys={[
          { key: "gap_between_calls", label: "Gap between calls" },
          { key: "caller_hold", label: "Caller hold" },
        ]}
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Queue-wide avg gap" value={fmtMs(qw?.avg_gap_s)} sub={`${qw?.gaps ?? 0} gaps · median ${fmtMs(qw?.median_gap_s)}`} />
        <StatCard label="Queue-wide max gap" value={fmtMs(qw?.max_gap_s)} />
        <StatCard label="Caller hold (avg)" value={fmtMs(hold?.avg_hold_s)} sub={`${hold?.calls ?? 0} answered calls`} />
        <StatCard label="Reps with gaps" value={String(data?.per_agent.length ?? 0)} />
      </div>
      <h2 className="text-lg font-semibold mb-2">Per agent — idle gap between answered calls</h2>
      <div className="bg-white rounded-lg shadow overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Rep</th>
              <th className="px-4 py-2 text-right">Gaps</th>
              <th className="px-4 py-2 text-right font-semibold">Average</th>
              <th className="px-4 py-2 text-right">Median</th>
              <th className="px-4 py-2 text-right">Longest</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.per_agent ?? []).length === 0 && <EmptyRow cols={5} text="No back-to-back calls in this range." />}
            {(data?.per_agent ?? []).map((r) => (
              <tr key={r.agent_key}>
                <td className="px-4 py-2 font-medium">{r.agent_name}</td>
                <td className="px-4 py-2 text-right font-mono">{r.gaps}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMs(r.avg_gap_s)}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMs(r.median_gap_s)}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMs(r.max_gap_s)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2 className="text-lg font-semibold mb-2">Caller hold before answer — by queue</h2>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Campaign</th>
              <th className="px-4 py-2 text-right">Answered calls</th>
              <th className="px-4 py-2 text-right">Avg hold</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.caller_hold_by_campaign ?? []).length === 0 && <EmptyRow cols={3} text="No answered calls in this range." />}
            {(data?.caller_hold_by_campaign ?? []).map((r, i) => (
              <tr key={i}>
                <td className="px-4 py-2 font-medium">{r.campaign_name ?? (r.campaign_id != null ? `Campaign ${r.campaign_id}` : "Unknown queue")}</td>
                <td className="px-4 py-2 text-right font-mono">{r.calls}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMs(r.avg_hold_s)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function WorkforcePage() {
  const [tab, setTab] = useState<Tab>("payroll");
  // Empty = "today in the system zone" — the API decides what today is, so the
  // page never has to know the zone before its first response.
  const [range, setRange] = useState<RangeParams>({});

  const agentDaysEnabled = tab === "payroll" || tab === "hours" || tab === "calls";
  const agentDays = useQuery({
    queryKey: ["wf-agent-days", range],
    queryFn: () => getAgentDays(range),
    placeholderData: keepPreviousData,
    enabled: agentDaysEnabled,
  });

  const meta = agentDays.data?.meta;
  const tz = meta?.timezone;
  const shownFrom = range.date_from ?? meta?.date_from ?? "";
  const shownTo = range.date_to ?? meta?.date_to ?? "";

  const presets = useMemo(() => {
    if (!tz) return [];
    const today = localDateInZone(tz);
    return [
      { label: "Today", value: {} as RangeParams },
      { label: "Yesterday", value: { date_from: localDateInZone(tz, 1), date_to: localDateInZone(tz, 1) } },
      { label: "Last 7 days", value: { date_from: localDateInZone(tz, 6), date_to: today } },
      { label: "Last 14 days", value: { date_from: localDateInZone(tz, 13), date_to: today } },
    ];
  }, [tz]);

  const rows = agentDays.data?.rows ?? [];

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Workforce</h1>
          <p className="text-sm text-gray-500">
            Payroll, output, abandon rate, wait time and transfers.
            {tz ? (
              <>
                {" "}All times are <span className="font-medium">{tz}</span>; report days run midnight to midnight in that zone.
              </>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label htmlFor="wf-from" className="text-gray-600">From</label>
          <input
            id="wf-from"
            type="date"
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={shownFrom}
            max={shownTo || undefined}
            onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value || undefined }))}
          />
          <label htmlFor="wf-to" className="text-gray-600">To</label>
          <input
            id="wf-to"
            type="date"
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            value={shownTo}
            min={shownFrom || undefined}
            onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value || undefined }))}
          />
          {presets.map((p) => (
            <button
              key={p.label}
              type="button"
              className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
              onClick={() => setRange(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 whitespace-nowrap ${
              tab === t.id ? "border-brand text-brand" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {agentDaysEnabled && agentDays.isError && (
        <p className="text-red-600 text-sm mb-3">{(agentDays.error as Error).message}</p>
      )}
      {agentDaysEnabled && agentDays.isLoading && !agentDays.data && (
        <p className="text-gray-500 text-sm">Loading…</p>
      )}

      {tab === "payroll" && <PayrollTab rows={rows} meta={meta} />}
      {tab === "hours" && <HoursTab rows={rows} meta={meta} />}
      {tab === "calls" && <CallsTab rows={rows} meta={meta} />}
      {tab === "abandon" && <AbandonTab range={range} />}
      {tab === "gaps" && <GapsTab range={range} />}
      {tab === "transfers" && <WorkforceTransfersTab range={range} />}
    </div>
  );
}
